// Per-mirror-client detail (Debt Lookup) — read-mode config by default,
// "Edit" toggles to an inline form (active + rps_cap only), plus the
// X-API-Key rotate section and a small 7-day mirror-traffic summary.
//
// Mirrors pages/ClientDetail.tsx with pricing config + decision/yield charts
// removed (the mirror has neither). The one-time-key rotate UX (confirm →
// reveal → copy → hide, persisted in sessionStorage) is copied verbatim.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  api,
  type MirrorClientDetailResponse,
  type MirrorClientPatchRequest,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const SESSION_KEY_TTL_MS = 30 * 60 * 1000;

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

export default function MirrorClientDetail() {
  const params = useParams();
  const id = params["id"] ?? "";
  const sessionKey = `loop-rtb:mirror-apikey:${id}`;

  const [client, setClient] = useState<MirrorClientDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Plaintext key — surfaced after rotate, persisted in sessionStorage.
  const [rotatedKeyPlaintext, setRotatedKeyState] = useState<string | null>(
    () => readSessionKey(sessionKey),
  );
  const setRotatedKeyPlaintext = (val: string | null): void => {
    setRotatedKeyState(val);
    try {
      if (val === null) sessionStorage.removeItem(sessionKey);
      else
        sessionStorage.setItem(
          sessionKey,
          JSON.stringify({ value: val, ts: Date.now() }),
        );
    } catch {
      /* sessionStorage may be unavailable in private mode */
    }
  };
  const [rotating, setRotating] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [copied, setCopied] = useState(false);

  // Configuration view-vs-edit mode.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<MirrorClientPatchRequest>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    api
      .getMirrorClient(id)
      .then((c) => {
        if (cancelled) return;
        setClient(c);
        setForm({ active: c.active, rps_cap: c.rps_cap });
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onRotate = async (): Promise<void> => {
    if (!id) return;
    setRotating(true);
    setError(null);
    try {
      const res = await api.rotateMirrorKey(id);
      setRotatedKeyPlaintext(res.api_key_plaintext);
      setConfirmingRotate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Generate failed");
    } finally {
      setRotating(false);
    }
  };

  const onCopyKey = async (): Promise<void> => {
    if (!rotatedKeyPlaintext) return;
    try {
      await navigator.clipboard.writeText(rotatedKeyPlaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard not permitted */
    }
  };

  const onSave = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const updated = await api.patchMirrorClient(id, form);
      setClient((prev) => (prev ? { ...prev, ...updated } : prev));
      setSaveMessage("Saved.");
      setEditing(false);
      setTimeout(() => setSaveMessage(null), 2400);
    } catch (err) {
      setSaveMessage(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onCancelEdit = (): void => {
    if (!client) return;
    setForm({ active: client.active, rps_cap: client.rps_cap });
    setEditing(false);
    setSaveMessage(null);
  };

  if (loading && !client) {
    return (
      <div>
        <PageHeader title={<Skeleton width={220} height={26} />} />
        <Skeleton height={140} />
      </div>
    );
  }

  if (error && !client) {
    return (
      <Card>
        <EmptyState title="Failed to load client" hint={error} />
      </Card>
    );
  }

  if (!client) {
    return (
      <Card>
        <EmptyState title="Client not found" />
      </Card>
    );
  }

  return (
    <div>
      <PageHeader
        title={client.client_name}
        subtitle={
          <span style={{ fontFamily: tokens.font.mono }}>
            {client.client_id}
          </span>
        }
        actions={
          <Pill tone={client.active ? "success" : "neutral"}>
            {client.active ? "Active" : "Disabled"}
          </Pill>
        }
      />

      {/* ───────────── Configuration ───────────── */}
      <Card
        title="Configuration"
        action={
          editing ? null : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              style={buttonStyle("ghost")}
            >
              Edit
            </button>
          )
        }
        style={{ marginBottom: tokens.space.xl }}
      >
        {editing ? (
          <form onSubmit={onSave}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: tokens.space.lg,
              }}
            >
              <div>
                <FieldLabel htmlFor="mcfg-active">Active</FieldLabel>
                <label
                  style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  <input
                    id="mcfg-active"
                    type="checkbox"
                    checked={form.active ?? false}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, active: e.target.checked }))
                    }
                  />
                  <span style={{ color: tokens.color.fg1 }}>
                    {form.active ? "Accepting traffic" : "Paused"}
                  </span>
                </label>
              </div>
              <div>
                <FieldLabel htmlFor="mcfg-rps">RPS cap</FieldLabel>
                <input
                  id="mcfg-rps"
                  type="number"
                  min="1"
                  max="500"
                  value={form.rps_cap ?? 0}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, rps_cap: Number(e.target.value) }))
                  }
                  style={inputStyle}
                />
              </div>
            </div>
            <div
              style={{
                marginTop: tokens.space.lg,
                display: "flex",
                gap: tokens.space.sm,
                alignItems: "center",
              }}
            >
              <button
                type="submit"
                disabled={saving}
                style={buttonStyle("primary", saving)}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                disabled={saving}
                style={buttonStyle("ghost", saving)}
              >
                Cancel
              </button>
              {saveMessage && (
                <span
                  style={{ color: tokens.color.fg1, fontSize: tokens.font.size.sm }}
                >
                  {saveMessage}
                </span>
              )}
            </div>
          </form>
        ) : (
          <ReadOnlyConfig client={client} />
        )}
      </Card>

      {/* ───────────── X-API-Key ───────────── */}
      <Card title="X-API-Key" style={{ marginBottom: tokens.space.xl }}>
        <p
          style={{
            color: tokens.color.fg1,
            margin: `0 0 ${tokens.space.md}px`,
            fontSize: tokens.font.size.base,
          }}
        >
          Only an HMAC of each key is stored — the plaintext can't be recovered.
          Generate a new key when you need a usable value; the previous key
          stops working immediately.
        </p>

        {rotatedKeyPlaintext ? (
          <div style={apiKeyCard}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: tokens.space.sm,
              }}
            >
              <span
                style={{
                  fontSize: tokens.font.size.xs,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: tokens.color.tealLight,
                  fontWeight: 600,
                }}
              >
                Active plaintext
              </span>
              <span
                style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}
              >
                Cleared on tab close
              </span>
            </div>
            <pre style={keyPre}>
              <code>{rotatedKeyPlaintext}</code>
            </pre>
            <div
              style={{
                marginTop: tokens.space.sm,
                display: "flex",
                gap: tokens.space.sm,
              }}
            >
              <button
                type="button"
                onClick={() => void onCopyKey()}
                style={buttonStyle("subtle")}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
              <button
                type="button"
                onClick={() => setRotatedKeyPlaintext(null)}
                style={buttonStyle("ghost")}
              >
                Hide
              </button>
            </div>
          </div>
        ) : null}

        {confirmingRotate ? (
          <div style={confirmCard}>
            <div style={{ marginBottom: tokens.space.md }}>
              <strong style={{ color: tokens.color.fg0 }}>
                Generate a new X-API-Key?
              </strong>
              <div
                style={{
                  color: tokens.color.fg1,
                  marginTop: tokens.space.xs,
                  fontSize: tokens.font.size.sm,
                }}
              >
                The current key stops working immediately. Share the new key
                with the integration partner.
              </div>
            </div>
            <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => void onRotate()}
                disabled={rotating}
                style={buttonStyle("primary", rotating)}
              >
                {rotating ? "Generating…" : "Yes, generate new key"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRotate(false)}
                disabled={rotating}
                style={buttonStyle("ghost", rotating)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingRotate(true)}
            disabled={rotating}
            style={buttonStyle("primary", rotating)}
          >
            {rotatedKeyPlaintext ? "Generate another key" : "Generate new key"}
          </button>
        )}
      </Card>

      {/* ───────────── Last 7 days ───────────── */}
      <Card title="Last 7 days">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: tokens.space.lg,
          }}
        >
          <StatCard
            label="API Calls"
            value={(client.stats7d?.totalCalls ?? 0).toLocaleString()}
            hint="evt: mirror.lookup"
          />
          <StatCard
            label="Cache hit rate"
            value={fmtPct(client.stats7d?.cacheHitRate)}
            hint="served from credit cache"
          />
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only configuration (default state)
// ---------------------------------------------------------------------------

function ReadOnlyConfig({ client }: { client: MirrorClientDetailResponse }) {
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: tokens.space.lg,
        margin: 0,
      }}
    >
      <ReadField label="Status">
        <Pill tone={client.active ? "success" : "neutral"}>
          {client.active ? "Accepting traffic" : "Paused"}
        </Pill>
      </ReadField>
      <ReadField label="RPS cap" mono>
        {client.rps_cap}
      </ReadField>
    </dl>
  );
}

function ReadField({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt
        style={{
          fontSize: tokens.font.size.xs,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.color.fg1,
          fontWeight: 600,
          marginBottom: tokens.space.xs,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          color: tokens.color.fg0,
          fontSize: tokens.font.size.lg,
          fontWeight: 600,
          ...(mono
            ? {
                fontFamily: tokens.font.mono,
                fontVariantNumeric: "tabular-nums",
              }
            : {}),
        }}
      >
        {children}
      </dd>
    </div>
  );
}

function readSessionKey(storageKey: string): string | null {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { value?: string; ts?: number };
    if (typeof parsed.value !== "string" || typeof parsed.ts !== "number")
      return null;
    if (Date.now() - parsed.ts > SESSION_KEY_TTL_MS) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

const apiKeyCard: React.CSSProperties = {
  marginBottom: tokens.space.md,
  padding: tokens.space.lg,
  background: tokens.color.bg1,
  border: "1px solid rgba(0,212,170,0.30)",
  borderRadius: tokens.radius.md,
  boxShadow: "0 0 0 1px rgba(0,212,170,0.06)",
};

const confirmCard: React.CSSProperties = {
  marginBottom: tokens.space.md,
  padding: tokens.space.lg,
  background: tokens.color.bg3,
  border: `1px solid ${tokens.color.border}`,
  borderRadius: tokens.radius.md,
};

const keyPre: React.CSSProperties = {
  margin: 0,
  padding: "10px 12px",
  background: tokens.color.bg0,
  color: tokens.color.tealLight,
  borderRadius: tokens.radius.sm,
  fontFamily: tokens.font.mono,
  fontSize: tokens.font.size.sm,
  overflow: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
  userSelect: "all",
};
